// SchrankDoor - Holzschrank-Visualisierung mit Türrichtung
//
// tuer_richtung: 'SUED' (default) | 'NORD' | 'OST' | 'WEST'
// Die Türfront zeigt in die angegebene Himmelsrichtung.
// Auf der Front: Türspalt, Doppel-Griffe, dezenter Schwung-Bogen.
// Status-LED + Rückwand-Maserung auf der gegenüberliegenden Seite.
import React from 'react';

const STATUS_COLORS = {
  FREI:        { led: '#10b981', glow: '#10b98144', label: 'Frei' },
  BELEGT:      { led: '#f97316', glow: '#f9731644', label: 'Belegt' },
  DEAKTIVIERT: { led: '#94a3b8', glow: '#94a3b822', label: 'Deaktiviert' },
  RESERVIERT:  { led: '#eab308', glow: '#eab30844', label: 'Reserviert' },
};

// Verschiedene Holzfarben je nach Typ - alle warmes Holz
const TYP_WOOD_COLORS = {
  PERSOENLICH: { top: '#a16937', mid: '#8b5a2b', bot: '#704826', grain: '#5d3a1c' },  // Eiche
  WASCHRAUM:   { top: '#c89b76', mid: '#a87a55', bot: '#86603f', grain: '#634528' },  // Buche hell
  ESSENSRAUM:  { top: '#9a6a3f', mid: '#7a4f2a', bot: '#5d3a1c', grain: '#3e2614' },  // Kiefer dunkel
  GEMEINSCHAFT:{ top: '#b48157', mid: '#945e3a', bot: '#6e4524', grain: '#4a2e16' },  // Nussbaum
  CUSTOM:      { top: '#b4895c', mid: '#946838', bot: '#704a23', grain: '#4f2f14' },  // Standard Holz
};

export function SchrankDoor({
  schrank,
  geometry,
  onClick,
  onHoverEnter,
  onHoverLeave,
  selected,
  highlighted,
  showLabel = true,
  compact = false,  // wenn true: kein eigener Schatten/Maserung — Teil einer Wand
}) {
  // Container-Schränke: spezielle Darstellung (Wand mit Raster-Hinweis)
  if (schrank.ist_container) {
    return (
      <SchrankContainerBlock
        schrank={schrank}
        geometry={geometry}
        onClick={onClick}
        onHoverEnter={onHoverEnter}
        onHoverLeave={onHoverLeave}
        selected={selected}
        highlighted={highlighted}
        showLabel={showLabel}
      />
    );
  }
  const g = geometry || schrank.geometry || { x: 0, y: 0, width: 40, height: 80 };
  const { x, y, width: w, height: h } = g;
  const stat = STATUS_COLORS[schrank.status] || STATUS_COLORS.FREI;
  const wood = TYP_WOOD_COLORS[schrank.typ] || TYP_WOOD_COLORS.PERSOENLICH;
  const dir = schrank.tuer_richtung || 'SUED';

  // Eindeutige Gradient-IDs
  const woodGrad = `wood-${schrank.id}-grad`;
  const ledGrad = `led-${schrank.id}-grad`;

  // Tür-Richtung definiert Achse
  const horizontal = dir === 'SUED' || dir === 'NORD'; // Türen-Spalt vertikal
  // const frontEdge = dir; // wo die Front-Kante ist

  // Position der Front-Kante in Bezug auf Rechteck
  // SUED: untere Kante, NORD: obere, OST: rechte, WEST: linke
  // Spaltlinie (zweiflüglige Tür) verläuft senkrecht zur Front-Kante,
  // beginnt am Front-Kanten-Mittelpunkt und geht in den Schrank rein
  // (wir zeichnen aber die volle Spalt-Linie, da Türen sich öffnen)
  let splitStart, splitEnd, handle1, handle2, ledPos, swingPaths;
  const inset = Math.min(w, h) * 0.08;
  const handleSize = Math.max(2, Math.min(w, h) * 0.06);

  if (dir === 'SUED') {
    // Front unten: Spalt vertikal mittig
    splitStart = { x: x + w / 2, y: y + inset };
    splitEnd   = { x: x + w / 2, y: y + h - inset };
    // Griffe nahe der Front (unten), links/rechts vom Spalt
    handle1 = { cx: x + w * 0.30, cy: y + h - inset * 1.5 };
    handle2 = { cx: x + w * 0.70, cy: y + h - inset * 1.5 };
    ledPos  = { cx: x + w - inset, cy: y + inset };
    // Schwung-Bogen für offene Türen (Andeutung)
    swingPaths = [
      `M ${x + w/2} ${y + h} A ${w/2} ${w/2} 0 0 1 ${x} ${y + h/2}`,
      `M ${x + w/2} ${y + h} A ${w/2} ${w/2} 0 0 0 ${x + w} ${y + h/2}`,
    ];
  } else if (dir === 'NORD') {
    splitStart = { x: x + w / 2, y: y + inset };
    splitEnd   = { x: x + w / 2, y: y + h - inset };
    handle1 = { cx: x + w * 0.30, cy: y + inset * 1.5 };
    handle2 = { cx: x + w * 0.70, cy: y + inset * 1.5 };
    ledPos  = { cx: x + w - inset, cy: y + h - inset };
    swingPaths = [
      `M ${x + w/2} ${y} A ${w/2} ${w/2} 0 0 0 ${x} ${y + h/2}`,
      `M ${x + w/2} ${y} A ${w/2} ${w/2} 0 0 1 ${x + w} ${y + h/2}`,
    ];
  } else if (dir === 'OST') {
    // Front rechts: Spalt horizontal mittig
    splitStart = { x: x + inset, y: y + h / 2 };
    splitEnd   = { x: x + w - inset, y: y + h / 2 };
    handle1 = { cx: x + w - inset * 1.5, cy: y + h * 0.30 };
    handle2 = { cx: x + w - inset * 1.5, cy: y + h * 0.70 };
    ledPos  = { cx: x + inset, cy: y + inset };
    swingPaths = [
      `M ${x + w} ${y + h/2} A ${h/2} ${h/2} 0 0 1 ${x + w/2} ${y + h}`,
      `M ${x + w} ${y + h/2} A ${h/2} ${h/2} 0 0 0 ${x + w/2} ${y}`,
    ];
  } else { // WEST
    splitStart = { x: x + inset, y: y + h / 2 };
    splitEnd   = { x: x + w - inset, y: y + h / 2 };
    handle1 = { cx: x + inset * 1.5, cy: y + h * 0.30 };
    handle2 = { cx: x + inset * 1.5, cy: y + h * 0.70 };
    ledPos  = { cx: x + w - inset, cy: y + inset };
    swingPaths = [
      `M ${x} ${y + h/2} A ${h/2} ${h/2} 0 0 0 ${x + w/2} ${y}`,
      `M ${x} ${y + h/2} A ${h/2} ${h/2} 0 0 1 ${x + w/2} ${y + h}`,
    ];
  }

  const ledR = Math.max(1.2, Math.min(w, h) * 0.035);
  // Größere Schrift in kompakter Spind-Optik, damit die Nummer auch ohne
  // Hover gut lesbar ist.
  const fs = compact
    ? Math.max(8, Math.min(w / 3.2, h / 3.8, 22))
    : Math.max(6, Math.min(w / 5, h / 7, 12));
  const showSwing = Math.min(w, h) >= 30;
  // Einflügelige Tür bei kleinen Fächern in einer Schrankwand
  // (typisches Spind-Raster mit schmalen Einzelfächern).
  const singleDoor = compact && Math.min(w, h) < 50;
  // Position des Griffes bei Einzeltür: an der Front-Kante, leicht zur Seite
  // (typische Spind-Optik mit Drehgriff)
  let singleHandle = handle1;
  if (singleDoor) {
    if (dir === 'SUED') singleHandle = { cx: x + w - inset * 1.4, cy: y + h - inset * 1.6 };
    else if (dir === 'NORD') singleHandle = { cx: x + inset * 1.4, cy: y + inset * 1.6 };
    else if (dir === 'OST') singleHandle = { cx: x + w - inset * 1.6, cy: y + h - inset * 1.4 };
    else singleHandle = { cx: x + inset * 1.6, cy: y + inset * 1.4 };
  }

  // Label-Stack: Name + Abteilung (wenn vorhanden)
  const labelLines = [schrank.name];
  if (schrank.mitarbeiter_name && schrank.abteilung_name) {
    labelLines.push(schrank.abteilung_name);
  } else if (schrank.abteilung_name) {
    labelLines.push(schrank.abteilung_name);
  }

  // Label-Position: kompakter Spind-Modus → Mittig im Fach (gut sichtbar)
  // Sonst nahe der Front-Kante (für räumliche Wirkung im Floorplan).
  let labelX, labelY, labelAnchor;
  if (compact) {
    labelX = x + w / 2; labelY = y + h / 2; labelAnchor = 'middle';
  } else if (dir === 'SUED') {
    labelX = x + w / 2; labelY = y + h * 0.30; labelAnchor = 'middle';
  } else if (dir === 'NORD') {
    labelX = x + w / 2; labelY = y + h * 0.70; labelAnchor = 'middle';
  } else if (dir === 'OST') {
    labelX = x + w * 0.30; labelY = y + h / 2; labelAnchor = 'middle';
  } else {
    labelX = x + w * 0.70; labelY = y + h / 2; labelAnchor = 'middle';
  }

  return (
    <g
      onClick={(e) => { e.stopPropagation(); onClick?.(schrank); }}
      onPointerEnter={(e) => onHoverEnter?.(schrank, e)}
      onPointerLeave={() => onHoverLeave?.()}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        opacity: highlighted === false ? 0.4 : 1,
        transition: 'opacity 0.2s',
      }}
      data-testid={`schrank-door-${schrank.id}`}
    >
      <defs>
        <linearGradient id={woodGrad}
                        x1={horizontal ? '0%' : '0%'}
                        y1={horizontal ? '0%' : '0%'}
                        x2={horizontal ? '0%' : '100%'}
                        y2={horizontal ? '100%' : '0%'}>
          <stop offset="0%" stopColor={wood.top} />
          <stop offset="50%" stopColor={wood.mid} />
          <stop offset="100%" stopColor={wood.bot} />
        </linearGradient>
        <radialGradient id={ledGrad}>
          <stop offset="0%" stopColor={stat.led} stopOpacity="1" />
          <stop offset="70%" stopColor={stat.led} stopOpacity="0.6" />
          <stop offset="100%" stopColor={stat.glow} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Tür-Öffnungs-Schwung (dezent, hinter Korpus) */}
      {!compact && showSwing && swingPaths.map((d, i) => (
        <path key={i} d={d}
              fill={stat.led}
              fillOpacity={0.05}
              stroke={stat.led}
              strokeOpacity={0.3}
              strokeWidth={0.6}
              strokeDasharray="2 2" />
      ))}

      {/* Schatten - nur Einzel-Modus */}
      {!compact && (
        <rect
          x={x + 1} y={y + 2} width={w} height={h}
          fill="rgba(0,0,0,0.22)"
          rx={Math.min(2, w * 0.05)}
        />
      )}
      {/* Korpus (Holz) - kein Fill in compact, da Wand-BG bereits gemalt */}
      <rect
        x={x} y={y} width={w} height={h}
        fill={compact ? 'rgba(0,0,0,0)' : `url(#${woodGrad})`}
        stroke={selected ? '#dc2626' : (compact ? 'rgba(0,0,0,0.4)' : '#3a2010')}
        strokeWidth={selected ? 2 : (compact ? 0.5 : 0.8)}
        rx={compact ? 0 : Math.min(2, w * 0.05)}
      />
      {/* Holz-Maserung (nur Einzelmodus) */}
      {!compact && Math.min(w, h) >= 20 && (
        <>
          {horizontal ? (
            // Maserung vertikal (Türfront unten/oben)
            <>
              <line x1={x + w * 0.2} y1={y + 2} x2={x + w * 0.2} y2={y + h - 2}
                    stroke={wood.grain} strokeOpacity={0.25} strokeWidth={0.4} />
              <line x1={x + w * 0.5} y1={y + 2} x2={x + w * 0.5} y2={y + h - 2}
                    stroke={wood.grain} strokeOpacity={0.18} strokeWidth={0.3} />
              <line x1={x + w * 0.8} y1={y + 2} x2={x + w * 0.8} y2={y + h - 2}
                    stroke={wood.grain} strokeOpacity={0.25} strokeWidth={0.4} />
            </>
          ) : (
            <>
              <line x1={x + 2} y1={y + h * 0.2} x2={x + w - 2} y2={y + h * 0.2}
                    stroke={wood.grain} strokeOpacity={0.25} strokeWidth={0.4} />
              <line x1={x + 2} y1={y + h * 0.5} x2={x + w - 2} y2={y + h * 0.5}
                    stroke={wood.grain} strokeOpacity={0.18} strokeWidth={0.3} />
              <line x1={x + 2} y1={y + h * 0.8} x2={x + w - 2} y2={y + h * 0.8}
                    stroke={wood.grain} strokeOpacity={0.25} strokeWidth={0.4} />
            </>
          )}
        </>
      )}
      {/* Status-Band an der Front-Kante */}
      {dir === 'SUED' && (
        <rect x={x} y={y + h - 3} width={w} height={3} fill={stat.led} opacity={0.85} />
      )}
      {dir === 'NORD' && (
        <rect x={x} y={y} width={w} height={3} fill={stat.led} opacity={0.85} />
      )}
      {dir === 'OST' && (
        <rect x={x + w - 3} y={y} width={3} height={h} fill={stat.led} opacity={0.85} />
      )}
      {dir === 'WEST' && (
        <rect x={x} y={y} width={3} height={h} fill={stat.led} opacity={0.85} />
      )}

      {/* Tür-Spaltlinie — nur bei zweiflügeliger Tür */}
      {!singleDoor && (
        <line
          x1={splitStart.x} y1={splitStart.y}
          x2={splitEnd.x}   y2={splitEnd.y}
          stroke="rgba(0,0,0,0.55)" strokeWidth={0.6}
        />
      )}

      {/* Türgriffe: 2 Knäufe bei Doppeltür, 1 Knauf bei Einzeltür */}
      {singleDoor ? (
        <circle cx={singleHandle.cx} cy={singleHandle.cy} r={handleSize / 2}
                fill="#fbbf24" stroke="#7c4a03" strokeWidth={0.3} />
      ) : (
        <>
          <circle cx={handle1.cx} cy={handle1.cy} r={handleSize / 2}
                  fill="#fbbf24" stroke="#7c4a03" strokeWidth={0.3} />
          <circle cx={handle2.cx} cy={handle2.cy} r={handleSize / 2}
                  fill="#fbbf24" stroke="#7c4a03" strokeWidth={0.3} />
        </>
      )}

      {/* LED auf der "Rückwand" (gegenüber Front-Kante) */}
      <circle cx={ledPos.cx} cy={ledPos.cy} r={ledR * 2.2} fill={`url(#${ledGrad})`} />
      <circle cx={ledPos.cx} cy={ledPos.cy} r={ledR} fill={stat.led}
              stroke="rgba(0,0,0,0.5)" strokeWidth={0.3} />

      {/* Labels */}
      {showLabel && w >= 16 && h >= 20 && (
        <g style={{ pointerEvents: 'none', userSelect: 'none' }}>
          {labelLines.map((line, i) => (
            <text
              key={i}
              x={labelX}
              y={labelY + (i - (labelLines.length - 1) / 2) * (fs + 1)}
              textAnchor={labelAnchor}
              dominantBaseline="central"
              fontSize={i === 0 ? fs : Math.max(fs * 0.75, 6)}
              fill="#fef3c7"
              fontWeight={i === 0 ? 700 : 500}
              stroke="rgba(0,0,0,0.4)"
              strokeWidth={i === 0 ? 0.4 : 0.25}
              paintOrder="stroke"
            >
              {line}
            </text>
          ))}
        </g>
      )}
    </g>
  );
}

// Container-Block (Schrankwand als großer Holz-Korpus mit Raster-Hinweis)
function SchrankContainerBlock({ schrank, geometry, onClick, onHoverEnter, onHoverLeave, selected, highlighted, showLabel }) {
  const g = geometry || schrank.geometry || { x: 0, y: 0, width: 100, height: 40 };
  const { x, y, width: w, height: h } = g;
  const wood = TYP_WOOD_COLORS[schrank.typ] || TYP_WOOD_COLORS.PERSOENLICH;
  const spalten = g.spalten || 0;
  const reihen = g.reihen || 0;
  const totalFaecher = spalten * reihen;
  const gradId = `container-${schrank.id}-grad`;

  return (
    <g
      onClick={(e) => { e.stopPropagation(); onClick?.(schrank); }}
      onPointerEnter={(e) => onHoverEnter?.(schrank, e)}
      onPointerLeave={() => onHoverLeave?.()}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        opacity: highlighted === false ? 0.4 : 1,
        transition: 'opacity 0.2s',
      }}
      data-testid={`schrank-container-${schrank.id}`}
    >
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={wood.top} />
          <stop offset="50%" stopColor={wood.mid} />
          <stop offset="100%" stopColor={wood.bot} />
        </linearGradient>
      </defs>
      {/* Schatten */}
      <rect x={x + 2} y={y + 3} width={w} height={h}
            fill="rgba(0,0,0,0.25)" rx={3} />
      {/* Korpus */}
      <rect x={x} y={y} width={w} height={h}
            fill={`url(#${gradId})`}
            stroke={selected ? '#dc2626' : '#3a2010'}
            strokeWidth={selected ? 2 : 1}
            rx={3} />
      {/* Raster-Andeutung (kleine Linien für Fächer) */}
      {spalten > 0 && reihen > 0 && Math.min(w, h) >= 20 && (() => {
        const lines = [];
        const colW = w / spalten;
        const rowH = h / reihen;
        for (let i = 1; i < spalten; i++) {
          lines.push(
            <line key={`c${i}`}
                  x1={x + i * colW} y1={y + 3}
                  x2={x + i * colW} y2={y + h - 3}
                  stroke="rgba(0,0,0,0.35)" strokeWidth={0.5} />
          );
        }
        for (let i = 1; i < reihen; i++) {
          lines.push(
            <line key={`r${i}`}
                  x1={x + 3} y1={y + i * rowH}
                  x2={x + w - 3} y2={y + i * rowH}
                  stroke="rgba(0,0,0,0.35)" strokeWidth={0.5} />
          );
        }
        return <g>{lines}</g>;
      })()}
      {/* Maserung */}
      <line x1={x + w * 0.25} y1={y + 2} x2={x + w * 0.25} y2={y + h - 2}
            stroke={wood.grain} strokeOpacity={0.2} strokeWidth={0.3} />
      <line x1={x + w * 0.75} y1={y + 2} x2={x + w * 0.75} y2={y + h - 2}
            stroke={wood.grain} strokeOpacity={0.2} strokeWidth={0.3} />
      {/* Label */}
      {showLabel && w >= 30 && h >= 16 && (() => {
        const fs = Math.max(8, Math.min(w / 12, h / 4, 14));
        return (
          <g style={{ pointerEvents: 'none', userSelect: 'none' }}>
            <text x={x + w / 2} y={y + h / 2 - (totalFaecher ? fs * 0.4 : 0)}
                  textAnchor="middle" dominantBaseline="central"
                  fontSize={fs} fontWeight={700}
                  fill="#fef3c7"
                  stroke="rgba(0,0,0,0.4)" strokeWidth={0.4} paintOrder="stroke">
              {schrank.name}
            </text>
            {totalFaecher > 0 && (
              <text x={x + w / 2} y={y + h / 2 + fs * 0.6}
                    textAnchor="middle" dominantBaseline="central"
                    fontSize={Math.max(fs * 0.7, 7)} fontWeight={500}
                    fill="#fef3c7" fillOpacity={0.85}>
                {totalFaecher} Fächer · zum Öffnen klicken
              </text>
            )}
          </g>
        );
      })()}
    </g>
  );
}

export function SchrankStatusBadge({ status, count }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.FREI;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className="w-2.5 h-2.5 rounded-full"
            style={{ background: s.led, boxShadow: `0 0 6px ${s.glow}` }} />
      <span className="text-muted-foreground">{s.label}</span>
      {count !== undefined && (
        <span className="font-semibold tabular-nums">{count}</span>
      )}
    </span>
  );
}

export default SchrankDoor;
